import { EmployerFooter, EmployerHeader } from '@seek/adv-header-footer';
import { Box, ContentBlock, Page, ToastProvider } from 'braid-design-system';
import { observer } from 'mobx-react';
import { type FC, type ReactNode, useEffect, useState } from 'react';

import { UnhandledErrorPageBlock } from 'src/components/app';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import {
  useFeatureFlagsQuery,
  useSearchParamsToFeatureFlags,
} from 'src/libs/FeatureFlags';
import { useHeaderFooterContextQuery } from 'src/libs/HeaderFooterContext';
import { createPageConfig, type PageConfig } from 'src/libs/PageConfig';
import { environments } from 'src/siteConfig';
import { useStores } from 'src/utils/hooks/useStores';

import AutoScrollIntoViewForAndroid from '../../components/common/AutoScrollIntoViewForAndroid';
// @ts-ignore
import connectMediaQuery from '../../components/hoc/connectMediaQuery';
import { CandidateManagementInitialiser } from '../../pages/CandidateManagementPage'; // FIXME: CandidateManagementInitialiser should live in the same folder as this file
import type { AppConfig } from '../App';
import { CandidateManagementAppSkeleton } from '../AppPageBlock/CandidateManagementAppSkeleton'; // FIXME: This should come from `../CandidateManagementApp` i.e. rename AppPageBlock and use index.ts to export
import {
  type CandidatesManagementPageAnalytics,
  createPageAnalytics,
} from '../AppPageBlock/createPageAnalytics';

import {
  FeatureFlagsProvider,
  featureKeys,
  type Features,
} from './FeatureFlagsContext';

const CandidateManagementPage: FC<{ config: AppConfig }> = ({
  config: { site, environment, version, branch },
}) => {
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);

  useEffect(() => {
    setPageConfig(
      // FIXME: createPageConfig creates another Hirer Graph client. We should try to migrate from `src/graphql/hirer/client.ts` to use the GraphQL client here.
      createPageConfig({
        site,
        environment,
        appName: 'candidate-management',
        version,
        branch,
        datadog: {
          applicationId: '2dbc7a59-bda4-49bb-9d07-7dd80c734e3e',
          clientToken: 'pub153ba3eefcb49962e8b2b318fa04beaa',
          service: 'cm-candidate-management-page',
        },
      }),
    );
  }, [site, environment, version, branch]);

  if (!pageConfig) {
    return <PageSkeleton />;
  }

  return <CandidateManagementApp pageConfig={pageConfig} />;
};

export default CandidateManagementPage;

// Note: isMobile is injected here by connectMediaQuery
const CandidateManagementBaseApp: FC<{
  isMobile: boolean;
  pageConfig: PageConfig;
}> = ({ isMobile, pageConfig }) => {
  const store = useStores();
  const { router, site, locale, environment } = useAppConfig();

  const [pageAnalytics, setStateAnalytics] = useState(() =>
    createPageAnalytics({
      locale,
      site,
      environment,
      userId: null,
      seekAdvertiserId: null,
      extraBaseAttributes: {
        currentPage: 'candidate list',
        testingId: 'loyalty-beta-experience',
      },
    }),
  );

  // Note 1: this call and the one in `src/graphql/hirer/schema/queryHeaderFooterContext.ts` (for MobX) must satisfy these conditions:
  // - this call's selection set must be the super set of MobX query
  // - variable shape must be the same
  // Reason: this is to force the MobX query to use the cache data fetched from this query.
  // Once we removed the MobX query, we can freely change the shape of the query.
  const { data, loading, error } = useHeaderFooterContextQuery({
    fetchPolicy: 'cache-first',
    onCompleted: ({ currentAdvertiser, headerFooterContext }) => {
      if (currentAdvertiser.__typename === 'MissingAdvertiserIdClaimError') {
        window.location.assign(
          router.accountSelect({
            returnUrl: router.candidates(
              // Note: we want to use the original search params blindly. If it's wrong, it's wrong. Any issue will be handled once the user comes back here.
              Object.fromEntries(
                new URLSearchParams(window.location.search),
              ) as unknown as Parameters<typeof router.candidates>[0],
            ),
          }),
        );
        return;
      }

      // FIXME: simplify this by making dataDogRum.setUser and createPageAnalytics handle undefined userId
      let userId: number | null = null;
      if (
        headerFooterContext?.__typename === 'HeaderFooterContext' &&
        headerFooterContext.user?.id
      ) {
        const parsedUserId = Number.parseInt(headerFooterContext.user.id, 10);
        if (!isNaN(parsedUserId)) {
          userId = parsedUserId;
        }
      }

      let isImpersonating = false;
      if (headerFooterContext?.__typename === 'HeaderFooterContext') {
        isImpersonating = Boolean(headerFooterContext.identityContext?.act);
      }

      pageConfig.dataDogRum.setUser({
        userId,
        advertiserId: currentAdvertiser.seekId,
        isImpersonating,
      });

      store.userStore.load({
        accountInfo: headerFooterContext,
        seekAdvertiserId: currentAdvertiser.seekId,
        addCustomError: pageConfig.dataDogRum.addCustomError,
      });
    },
  });

  const featureFlagsResult = useFeatureFlagsQuery({
    variables: { keys: featureKeys },
    skip: featureKeys.length === 0,
  });
  const featureFlagsOverrides =
    useSearchParamsToFeatureFlags<Features>(featureKeys);

  const isStaging = environment === environments.staging;

  useEffect(() => {
    store.setMatchMedia(isMobile);
  }, [store, isMobile]);

  if (error) {
    return (
      <CandidateManagementLayout
        header={
          <EmployerHeader
            activeTabId="jobs"
            site={site}
            locale={locale}
            isStaging={isStaging}
            error={error}
            divider
            onTrackedItemInteraction={(event) => {
              pageAnalytics.adapter.link({
                event: event.eventName,
                attributes: { ...event },
              });
            }}
          />
        }
        content={
          <ContentBlock>
            <Box margin="small">
              <UnhandledErrorPageBlock />
            </Box>
          </ContentBlock>
        }
        footer={
          <EmployerFooter site={site} locale={locale} isStaging={isStaging} />
        }
      />
    );
  }

  if (
    featureFlagsResult.loading ||
    loading ||
    !data ||
    data.currentAdvertiser.__typename === 'MissingAdvertiserIdClaimError'
  ) {
    return <PageSkeleton pageAnalytics={pageAnalytics} />;
  }

  return (
    <ToastProvider>
      <FeatureFlagsProvider
        // When `featureFlagsResult` errors, `data` would be `undefined` and coerced to `[]`
        // This means when we try to get the flag out from the provider, `variant` will be `'control'`
        featureFlags={featureFlagsResult.data?.featureFlags || []}
        overrides={featureFlagsOverrides}
      >
        <AutoScrollIntoViewForAndroid />
        <CandidateManagementInitialiser
          dataDogRum={pageConfig.dataDogRum}
          pageAnalytics={pageAnalytics}
          isMobile={isMobile}
          headerFooterContextData={data}
          setStateAnalytics={setStateAnalytics}
          seekAdvertiserId={data.currentAdvertiser.seekId}
        />
      </FeatureFlagsProvider>
    </ToastProvider>
  );
};
const CandidateManagementApp = connectMediaQuery(
  observer(CandidateManagementBaseApp),
);

/**
 * Static and Loading states
 */
const PageSkeleton: FC<{
  pageAnalytics?: CandidatesManagementPageAnalytics;
}> = ({ pageAnalytics }) => {
  const { site, locale, environment } = useAppConfig();

  const isStaging = environment === environments.staging;

  return (
    <CandidateManagementLayout
      header={
        <EmployerHeader
          activeTabId="jobs"
          site={site}
          locale={locale}
          isStaging={isStaging}
          divider
          onTrackedItemInteraction={
            pageAnalytics
              ? (event) => {
                  pageAnalytics.adapter.link({
                    event: event.eventName,
                    attributes: { event },
                  });
                }
              : undefined
          }
        />
      }
      content={<CandidateManagementAppSkeleton />}
      footer={
        <EmployerFooter site={site} locale={locale} isStaging={isStaging} />
      }
    />
  );
};

export const CandidateManagementLayout: FC<{
  header: ReactNode;
  content: ReactNode;
  footer: ReactNode;
}> = ({ header, content, footer }) => (
  <Page footer={footer}>
    {header}
    {content}
  </Page>
);
